import type { TaskRow } from '../api/tasks';

/** Local calendar day from API date (DATE or ISO), or null. */
export function startOfLocalDayFromTaskField(value: string | null | undefined): Date | null {
  if (value == null || String(value).trim() === '') return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1;
    const da = parseInt(m[3], 10);
    return new Date(y, mo, da, 0, 0, 0, 0);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** Inclusive [start, end] for highlighting; null if task has no start or due date. */
export function taskDateSpan(task: TaskRow): { start: Date; end: Date } | null {
  const s = startOfLocalDayFromTaskField(task.start_date);
  const e = startOfLocalDayFromTaskField(task.due_date);
  if (!s && !e) return null;
  const start = s ?? e!;
  const end = e ?? s!;
  if (start.getTime() > end.getTime()) return { start: end, end: start };
  return { start, end };
}

export function taskHue(taskId: number): number {
  return (taskId * 47) % 360;
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function dayCoversSpan(day: Date, span: { start: Date; end: Date }): boolean {
  const t = day.getTime();
  return t >= span.start.getTime() && t <= span.end.getTime();
}

export function isSpanStart(day: Date, span: { start: Date; end: Date }): boolean {
  return isSameLocalDay(day, span.start);
}

export function isSpanEnd(day: Date, span: { start: Date; end: Date }): boolean {
  return isSameLocalDay(day, span.end);
}

/** 42 cells: weeks starting Sunday, covering the month that contains (year, month0). */
export function calendarGridDates(year: number, month0: number): Date[] {
  const first = new Date(year, month0, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export function formatCalendarMonthTitle(year: number, month0: number): string {
  return new Date(year, month0, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

/** Local Sunday 00:00:00 for the week that contains `ref` (week starts Sunday, matches calendar grid). */
export function startOfLocalWeekSunday(ref: Date): Date {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), 0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

/** Seven consecutive local days starting at Sunday `weekStartSunday`. */
export function weekDatesFromSundayStart(weekStartSunday: Date): Date[] {
  const out: Date[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(weekStartSunday);
    d.setDate(weekStartSunday.getDate() + i);
    out.push(d);
  }
  return out;
}

/** e.g. "January 5–11, 2026" or "Dec 29, 2025 – Jan 4, 2026". */
export function formatWeekRangeTitle(weekStartSunday: Date): string {
  const end = new Date(weekStartSunday);
  end.setDate(weekStartSunday.getDate() + 6);
  const y0 = weekStartSunday.getFullYear();
  const y1 = end.getFullYear();
  const m0 = weekStartSunday.getMonth();
  const m1 = end.getMonth();
  if (y0 === y1 && m0 === m1) {
    const month = weekStartSunday.toLocaleDateString(undefined, { month: 'long' });
    return `${month} ${weekStartSunday.getDate()}–${end.getDate()}, ${y0}`;
  }
  const a = weekStartSunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const b = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${a} – ${b}`;
}
